import { Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { useRemoveMerchantFromWorkspace } from "@/api/admin/workspace-mutations";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { error, success } from "@/providers/ToastProvider";

type Props = {
  workspaceId: string;
  merchantId: string;
  /** Name shown in the confirmation body — either the brand or workspace name */
  name: string;
  /** "brand" → "Remove [name] from this workspace?" | "workspace" → "Remove brand from [name]?" */
  removing: "brand" | "workspace";
};

export const RemoveFromWorkspaceButton = ({ workspaceId, merchantId, name, removing }: Props) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { mutateAsync, isPending } = useRemoveMerchantFromWorkspace();

  const title = removing === "brand" ? "Remove merchant from workspace" : "Remove merchant from workspace";

  const description =
    removing === "brand"
      ? `This will remove ${name} from this workspace. Users in this workspace will no longer have access to this merchant.`
      : `This will remove this merchant from ${name}. Users in that workspace will no longer have access to this merchant.`;

  const onConfirm = async () => {
    try {
      await mutateAsync({ workspaceId, merchantId });
      success("Merchant removed from workspace");
      close();
    } catch {
      error("Failed to remove merchant from workspace");
    }
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={close}>
      <ButtonUtility
        size="xs"
        color="tertiary"
        icon={Trash01}
        tooltip="Remove from workspace"
        onClick={() => setOpen(true)}
      />
      <ModalOverlay isDismissable>
        <Modal className="dark-mode max-w-md">
          <Dialog>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all">
              <CloseButton onClick={close} size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    {title}
                  </Heading>
                </div>
                <p className="text-sm text-secondary">{description}</p>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={close}>
                  Cancel
                </Button>
                <Button color="primary-destructive" size="lg" type="button" isDisabled={isPending} onClick={onConfirm}>
                  Remove
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
